import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api, type ActiveCallout } from '../api-client';
import './CalloutBannerComponent.css';

interface CalloutBannerProps {
  page: string;
}

export function CalloutBannerComponent({ page }: CalloutBannerProps): JSX.Element | null {
  const [callouts, setCallouts] = useState<ActiveCallout[]>([]);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      try {
        const result = await api.getActiveCallouts(page);
        if (!cancelled && result.success) {
          setCallouts(result.data);
        }
      } catch {
        // Silently fail — callouts are non-critical
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [page]);

  const handleDismiss = async (id: string): Promise<void> => {
    // Optimistically remove from local state
    setCallouts((prev) => prev.filter((c) => c.id !== id));
    try {
      await api.dismissCallout(id);
    } catch {
      // Dismissal failed, but we already removed it optimistically
    }
  };

  if (callouts.length === 0) return null;

  return (
    <div className="callout-banners">
      {callouts.map((callout) => (
        <div key={callout.id} className={`callout-banner callout-${callout.type}`}>
          <div className="callout-banner-body">
            <div className="callout-banner-title">{callout.title}</div>
            <div className="callout-banner-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{callout.content}</ReactMarkdown>
            </div>
          </div>
          <button
            className="callout-banner-dismiss"
            onClick={() => void handleDismiss(callout.id)}
            title="Dismiss"
            aria-label="Dismiss callout"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
